import {
  CATEGORIES,
  DEPARTMENTS,
  newTicketSchema,
  OFFICE_LOCATIONS,
  SERVICE_MODE_LABELS,
  type Category,
} from '@helpdesk/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AppWindow,
  BookOpen,
  Check,
  ChevronDown,
  CircleHelp,
  Copy,
  Keyboard,
  Laptop,
  Mail,
  MailCheck,
  Monitor,
  PartyPopper,
  Printer,
  Wifi,
  type LucideIcon,
} from 'lucide-react';
import { useDeferredValue, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CoverNotices, KnownIssues } from '../components/Announcements.tsx';
import { Button, ButtonLink } from '../components/ui/button.tsx';
import { ChoiceCards, Field, Input, Select, Textarea } from '../components/ui/form.tsx';
import { Alert, Card, PageHeader, Spinner } from '../components/ui/misc.tsx';
import { AttachmentPicker } from '../components/ui/pickers.tsx';
import { api, ApiError, qs } from '../lib/api.ts';
import { useGoBack, useHomePath, usePageTitle } from '../lib/navigation.ts';
import { useOfficeStatus, type OfficeStatus } from '../lib/office.ts';
import { clearDraft, loadDraft, loadReporter, saveDraft, saveReporter, subjectFrom } from '../lib/report.ts';
import { useSession } from '../lib/session.ts';
import type { CreatedTicket, HeldTicket, KbArticle, User } from '../lib/types.ts';
import { validate, type FieldErrors } from '../lib/validate.ts';

/** One tap picks the problem type; short labels so they fit four across on a phone. */
const TILES: Record<Category, { label: string; icon: LucideIcon }> = {
  Internet: { label: 'Internet', icon: Wifi },
  Printer: { label: 'Printer', icon: Printer },
  Desktop: { label: 'Desktop', icon: Monitor },
  Laptop: { label: 'Laptop', icon: Laptop },
  Email: { label: 'Email', icon: Mail },
  Software: { label: 'Software', icon: AppWindow },
  Hardware: { label: 'Other device', icon: Keyboard },
  Other: { label: 'Something else', icon: CircleHelp },
};

const serviceOptions = [
  { value: 'it_visits', label: SERVICE_MODE_LABELS.it_visits, description: 'An IT officer comes to you.' },
  { value: 'user_visits', label: SERVICE_MODE_LABELS.user_visits, description: 'Bring the device to IT.' },
  {
    value: 'remote',
    label: SERVICE_MODE_LABELS.remote,
    description: 'IT calls you or guides you here. Handy when IT is away from the office.',
  },
];

const TEXT_MAX = 2000;

/** "on Monday at 08:00", "tomorrow at 08:00" or "today at 08:00". */
function whenText(iso: string) {
  const at = new Date(iso);
  const day = (d: Date) => d.toDateString();
  const tomorrow = new Date(Date.now() + 86_400_000);
  const time = at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (day(at) === day(new Date())) return `today at ${time}`;
  if (day(at) === day(tomorrow)) return `tomorrow at ${time}`;
  return `on ${at.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })} at ${time}`;
}

/** Outside office hours: the report still goes in, and IT picks it up when the office opens. */
function OfficeClosed({ status }: { status?: OfficeStatus }) {
  if (status?.breakUntil) {
    return (
      <Alert tone="info">IT is on lunch break. Send your report now; IT picks it up after the break.</Alert>
    );
  }
  if (!status || status.open || !status.nextOpening) return null;
  return (
    <Alert tone="info">
      The IT office is closed{status.holidayToday ? ` (${status.holidayToday})` : ''}. Send your report now;
      IT picks it up {whenText(status.nextOpening)}.
    </Alert>
  );
}

/** Published help articles that match what the person is typing. */
function Suggestions({ subject }: { subject: string }) {
  const q = useDeferredValue(subject.trim());
  const { data } = useQuery({
    queryKey: ['kb', 'suggest', q],
    queryFn: () => api.get<{ items: KbArticle[] }>(`/api/kb${qs({ q })}`),
    enabled: q.length >= 4,
    staleTime: 60_000,
  });
  const items = (data?.items ?? []).slice(0, 3);
  if (q.length < 4 || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-info/30 bg-info-soft/60 p-3.5">
      <p className="flex items-center gap-2 text-sm font-medium text-info">
        <BookOpen className="size-4" aria-hidden />
        These help articles might solve it straight away
      </p>
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((a) => (
          <li key={a.id}>
            <Link
              to={`/help/${a.id}`}
              target="_blank"
              className="text-text underline decoration-line underline-offset-2 hover:decoration-info"
            >
              {a.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CategoryTiles({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: Category) => void;
  error?: string;
}) {
  return (
    <div>
      <p id="category-label" className="mb-2 text-sm font-medium">
        What’s it about?
      </p>
      <div
        role="radiogroup"
        aria-labelledby="category-label"
        aria-invalid={!!error}
        aria-describedby={error ? 'category-error' : undefined}
        tabIndex={-1}
        className="grid grid-cols-4 gap-2 focus:outline-none"
      >
        {CATEGORIES.map((c) => {
          const { label, icon: Icon } = TILES[c];
          const on = value === c;
          return (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(c)}
              className={`flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-xl border px-1 py-3 text-center text-xs leading-tight font-medium transition-colors sm:text-sm ${
                on
                  ? 'border-brand bg-brand-soft text-brand-ink ring-1 ring-brand'
                  : 'border-line bg-surface text-text hover:border-brand/60 hover:bg-surface-2'
              }`}
            >
              <Icon className={`size-6 ${on ? 'text-brand' : 'text-muted'}`} aria-hidden />
              {label}
            </button>
          );
        })}
      </div>
      {error && (
        <p id="category-error" className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** A remembered detail ("Finance · FTC") with a Change button that opens the fields. */
function SummaryRow({ label, value, onChange }: { label: string; value: string; onChange: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-muted">{label}</p>
        <p className="truncate font-medium">{value}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={onChange}>
        Change
      </Button>
    </div>
  );
}

function Success({
  ticket,
  onDone,
  onAnother,
}: {
  ticket: CreatedTicket;
  onDone: () => void;
  onAnother: () => void;
}) {
  usePageTitle('Ticket sent');
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ticket.number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked; the number is on screen to copy by hand.
    }
  };
  return (
    <Card className="mx-auto max-w-lg p-8 text-center">
      <span className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand">
        <PartyPopper className="size-7" aria-hidden />
      </span>
      <h1 className="text-xl font-bold">Sent to IT</h1>
      <p className="mt-2 text-muted">Your ticket number is</p>
      <div className="mt-2 flex items-center justify-center gap-2">
        <span className="rounded-lg bg-surface-2 px-4 py-2 font-mono text-2xl font-bold tracking-wide">
          {ticket.number}
        </span>
        <Button variant="ghost" size="sm" onClick={copy} aria-label="Copy ticket number">
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </Button>
      </div>
      <p className="mt-4">
        Priority <strong>{ticket.priority}</strong>. IT will respond{' '}
        <strong>{ticket.estimatedResponse}</strong>.
      </p>
      <p className="mt-2 text-sm text-muted">You’ll be notified here and by email when IT replies.</p>
      <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
        <Button onClick={onDone}>Done</Button>
        <ButtonLink to={`/tickets/${ticket.number}`} variant="secondary" replace>
          View ticket
        </ButtonLink>
      </div>
      <Button variant="ghost" size="sm" className="mt-3" onClick={onAnother}>
        Report another problem
      </Button>
    </Card>
  );
}

/** After a guest submits: nothing reaches IT until they confirm from their email. */
function CheckEmail({
  held,
  onChangeAddress,
  onDone,
}: {
  held: HeldTicket;
  onChangeAddress: () => void;
  onDone: () => void;
}) {
  usePageTitle('Check your email');
  return (
    <Card className="mx-auto max-w-lg p-8 text-center">
      <span className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand">
        <MailCheck className="size-7" aria-hidden />
      </span>
      <h1 className="text-xl font-bold">Check your email to confirm</h1>
      <p className="mt-3">
        We’ve sent a link to <strong className="break-all">{held.email}</strong>. Your request reaches IT once
        you tap <strong>Confirm my request</strong> in that email.
      </p>
      <p className="mt-2 text-sm text-muted">
        The link works for {held.expiresInHours} hours. Can’t see it? Check your spam or junk folder.
      </p>
      <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
        <Button onClick={onDone}>Done</Button>
        <Button variant="secondary" onClick={onChangeAddress}>
          Wrong address? Change it
        </Button>
      </div>
      <p className="mt-6 border-t border-line pt-4 text-sm text-muted">
        Tip:{' '}
        <Link to="/sign-in" className="font-medium text-brand hover:underline">
          sign in
        </Link>{' '}
        once on this device and you won’t need to confirm next time.
      </p>
    </Card>
  );
}

/** The office printed on the poster whose QR code opened the form (?office=FTC). */
function useOfficeFromLink(): string {
  const [params] = useSearchParams();
  const office = params.get('office') ?? '';
  return (OFFICE_LOCATIONS as readonly string[]).includes(office) ? office : '';
}

export function NewTicketPage() {
  const { isLoading } = useSession();
  // Wait for the session: where-you-are comes from the profile.
  if (isLoading) return <Spinner />;
  return <ReportForm />;
}

/**
 * Report a problem: tap a type, say what's wrong, send. Everything else is
 * filled in from the profile or what this device remembers, with defaults
 * for the rest under "More options". What's typed is kept as a draft until
 * it's sent, so leaving the page loses nothing.
 */
function ReportForm() {
  const { user, isIt } = useSession();
  const home = useHomePath();
  const goBack = useGoBack(home);
  const officeFromLink = useOfficeFromLink();
  const office = useOfficeStatus();
  const formRef = useRef<HTMLFormElement>(null);

  // What the person is reporting, kept as a draft on this device.
  const [draft] = useState(loadDraft);
  const [category, setCategory] = useState(draft?.category ?? '');
  const [text, setText] = useState(draft?.text ?? '');
  const [personal, setPersonal] = useState(draft?.personal ?? false);
  const [serviceMode, setServiceMode] = useState(draft?.serviceMode ?? 'it_visits');
  // The server treats sub-3-second submissions as bots, so a restored draft
  // keeps the time typing actually began.
  const [startedAt, setStartedAt] = useState(() => draft?.startedAt ?? Date.now());
  const [restored, setRestored] = useState(!!(draft?.text || draft?.category));
  const [files, setFiles] = useState<File[]>([]);

  // Who and where: the profile, else what this device remembers. IT logging
  // for someone else starts blank.
  const [remembered] = useState(loadReporter);
  const mine = isIt ? {} : remembered;
  const [name, setName] = useState(user ? '' : (mine.name ?? ''));
  const [email, setEmail] = useState(user ? '' : (mine.email ?? ''));
  const [department, setDepartment] = useState((!isIt && user?.department) || mine.department || '');
  const [officeLocation, setOfficeLocation] = useState(
    officeFromLink || (!isIt && user?.officeLocation) || mine.officeLocation || '',
  );
  const [phone, setPhone] = useState((!isIt && user?.phone) || mine.phone || '');
  const [editYou, setEditYou] = useState(!user && !(name && email));
  const [editWhere, setEditWhere] = useState(isIt || !(department && officeLocation));
  const [more, setMore] = useState(false);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [created, setCreated] = useState<CreatedTicket | null>(null);
  const [held, setHeld] = useState<HeldTicket | null>(null);

  useEffect(() => {
    if (text.trim() || category) saveDraft({ category, text, personal, serviceMode, startedAt });
    else clearDraft();
  }, [category, text, personal, serviceMode, startedAt]);

  // IT logging for someone: pick a known person and their details fill in.
  const people = useQuery({
    queryKey: ['users', 'staff'],
    queryFn: () => api.get<{ items: User[] }>('/api/users?role=staff'),
    enabled: isIt,
    staleTime: 5 * 60_000,
  });
  const fillFromKnown = (value: string) => {
    const person = people.data?.items.find((p) => p.email === value.trim().toLowerCase());
    if (!person) return;
    if (person.name) setName(person.name);
    if (person.department) setDepartment(person.department);
    if (person.officeLocation) setOfficeLocation(person.officeLocation);
    if (person.phone) setPhone(person.phone);
  };

  const submit = useMutation({
    mutationFn: (body: FormData) => api.post<CreatedTicket | HeldTicket>('/api/tickets', body),
    onSuccess: (result) => {
      clearDraft();
      if (!isIt) saveReporter({ name, email, department, officeLocation, phone });
      if ('held' in result) setHeld(result);
      else setCreated(result);
      window.scrollTo({ top: 0 });
    },
    onError: (err) => {
      setErrors(err instanceof ApiError ? { ...err.fields, _: err.message } : { _: String(err) });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const startOver = () => {
    setCreated(null);
    setHeld(null);
    setCategory('');
    setText('');
    setPersonal(false);
    setServiceMode('it_visits');
    setFiles([]);
    setErrors({});
    setRestored(false);
    setStartedAt(Date.now());
    clearDraft();
  };

  if (created) return <Success ticket={created} onDone={goBack} onAnother={startOver} />;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const website = (e.currentTarget.elements.namedItem('website') as HTMLInputElement | null)?.value ?? '';
    const values: Record<string, string> = {
      name,
      email,
      department,
      officeLocation,
      phone,
      category,
      subject: subjectFrom(text),
      description: text.trim(),
      serviceMode,
      personal: String(personal),
    };
    const result = validate(newTicketSchema, values);
    const fields: FieldErrors = result.ok ? {} : { ...result.fields };
    if (fields.category) fields.category = 'Tap the kind of problem.';
    if (fields.subject || fields.description) {
      fields.text = fields.description ?? 'Please tell IT what’s wrong.';
      delete fields.subject;
      delete fields.description;
    }
    if (!user) {
      if (!name.trim()) fields.name = 'Please enter your full name.';
      if (!email.trim()) fields.email ??= 'Please enter your email: updates are sent there.';
    }
    if (Object.keys(fields).length) {
      setErrors(fields);
      if (fields.name || fields.email) setEditYou(true);
      if (fields.department || fields.officeLocation) setEditWhere(true);
      if (fields.phone || fields.serviceMode) setMore(true);
      // After React has marked the fields invalid, move focus to the first one.
      requestAnimationFrame(() =>
        formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
      );
      return;
    }
    setErrors({});
    const body = new FormData();
    for (const [k, v] of Object.entries(values)) if (v) body.set(k, v);
    if (website) body.set('website', website);
    body.set('formTs', String(startedAt));
    files.forEach((f) => body.append('attachments', f));
    submit.mutate(body);
  };

  const where = [department, officeLocation].filter(Boolean).join(' · ');

  return (
    <div className="mx-auto max-w-2xl">
      {held && (
        <CheckEmail
          held={held}
          onChangeAddress={() => {
            // The form is still filled in underneath; only the address needs fixing.
            setHeld(null);
            setEditYou(true);
            requestAnimationFrame(() =>
              formRef.current?.querySelector<HTMLElement>('[name="email"]')?.focus(),
            );
          }}
          onDone={goBack}
        />
      )}
      <div hidden={!!held}>
        <PageHeader
          back={home}
          title="Report a problem"
          description={
            user
              ? 'Tell IT what’s wrong. It takes a minute.'
              : 'No account needed. We’ll email you a link to confirm.'
          }
        />
        <form ref={formRef} onSubmit={onSubmit} noValidate className="space-y-4">
          {errors._ && <Alert tone="error">{errors._}</Alert>}
          <OfficeClosed status={office.data} />
          <CoverNotices />
          <KnownIssues />
          {restored && (
            <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted">
              We kept what you’d typed.
              <button type="button" onClick={startOver} className="font-medium text-brand hover:underline">
                Start over
              </button>
            </p>
          )}

          {isIt && (
            <Card className="space-y-4 p-5 sm:p-6">
              <div>
                <h2 className="font-semibold">Logging this for someone else?</h2>
                <p className="mt-0.5 text-sm text-muted">
                  Fill in their name and email, or leave both empty to report a problem of your own.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" error={errors.name}>
                  <Input
                    name="name"
                    autoComplete="off"
                    maxLength={100}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>
                <Field label="Email" error={errors.email}>
                  <Input
                    name="email"
                    type="email"
                    autoComplete="off"
                    inputMode="email"
                    list="known-people"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      fillFromKnown(e.target.value);
                    }}
                  />
                </Field>
                <datalist id="known-people">
                  {people.data?.items.map((p) => (
                    <option key={p.id} value={p.email}>
                      {p.name}
                    </option>
                  ))}
                </datalist>
              </div>
            </Card>
          )}

          <Card className="space-y-5 p-5 sm:p-6">
            <CategoryTiles
              value={category}
              onChange={(c) => {
                setCategory(c);
                setErrors(({ category: _, ...rest }) => rest);
              }}
              error={errors.category}
            />
            <Field
              label="What’s wrong?"
              error={errors.text}
              hint="What happened, any error message, and what you’ve tried. The first line becomes the title."
              aside={
                text.length > TEXT_MAX * 0.9 ? (
                  <span className="text-xs text-warning">
                    {text.length}/{TEXT_MAX}
                  </span>
                ) : undefined
              }
            >
              <Textarea
                name="text"
                rows={4}
                maxLength={TEXT_MAX}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. The printer in the Finance office shows a paper jam light, but there’s no paper stuck."
              />
            </Field>
            <Suggestions subject={subjectFrom(text)} />
            <AttachmentPicker
              compact
              label="Add a photo or file"
              files={files}
              onChange={setFiles}
              error={errors.attachments}
              onError={(message) => setErrors((e) => ({ ...e, attachments: message ?? '' }))}
            />
          </Card>

          <Card className="divide-y divide-line px-5 sm:px-6">
            {!user && (
              <div className="py-4">
                {editYou ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Your full name" error={errors.name} required>
                      <Input
                        name="name"
                        autoComplete="name"
                        maxLength={100}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Field>
                    <Field
                      label="Your email"
                      error={errors.email}
                      required
                      hint="We’ll send a link here to confirm, then updates."
                    >
                      <Input
                        name="email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Field>
                  </div>
                ) : (
                  <SummaryRow label="You" value={`${name} · ${email}`} onChange={() => setEditYou(true)} />
                )}
              </div>
            )}

            <div className="py-4">
              {editWhere ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Department" error={errors.department} required>
                    <Select
                      name="department"
                      options={DEPARTMENTS}
                      placeholder="Choose…"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                    />
                  </Field>
                  <Field label="Office location" error={errors.officeLocation} required>
                    <Select
                      name="officeLocation"
                      options={OFFICE_LOCATIONS}
                      placeholder="Choose…"
                      value={officeLocation}
                      onChange={(e) => setOfficeLocation(e.target.value)}
                    />
                  </Field>
                </div>
              ) : (
                <SummaryRow label="Where you are" value={where} onChange={() => setEditWhere(true)} />
              )}
            </div>

            <div className="py-2">
              <button
                type="button"
                aria-expanded={more}
                onClick={() => setMore((m) => !m)}
                className="-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-3 rounded-lg px-2 py-2 text-left hover:bg-surface-2"
              >
                <span>
                  <span className="block font-medium">More options</span>
                  <span className="block text-sm text-muted">
                    {SERVICE_MODE_LABELS[serviceMode as keyof typeof SERVICE_MODE_LABELS]}
                    {phone ? ` · ${phone}` : ''}
                    {personal ? ' · Personal request' : ''}
                  </span>
                </span>
                <ChevronDown
                  className={`size-5 shrink-0 text-muted transition-transform ${more ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {more && (
                <div className="space-y-4 pt-2 pb-3">
                  <ChoiceCards
                    name="serviceMode"
                    label="How would you like to be helped?"
                    required={false}
                    options={serviceOptions}
                    value={serviceMode}
                    onChange={setServiceMode}
                    error={errors.serviceMode}
                  />
                  <Field label="Phone number" error={errors.phone} hint="In case IT needs to call you.">
                    <Input
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      maxLength={25}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="sm:max-w-xs"
                    />
                  </Field>
                  <label className="flex items-start gap-3 rounded-lg border border-line p-3">
                    <input
                      type="checkbox"
                      checked={personal}
                      onChange={(e) => setPersonal(e.target.checked)}
                      className="mt-1 size-4 accent-(--brand)"
                    />
                    <span>
                      <span className="block font-medium">This is a personal request</span>
                      <span className="block text-sm text-muted">
                        Your own phone or device, or a personal favour.
                      </span>
                    </span>
                  </label>
                  {personal && (
                    <Alert tone="info" title="Personal requests wait for the personal-help slot">
                      {office.data?.personalSlot
                        ? `IT handles personal requests ${office.data.personalSlot}, after work requests.`
                        : 'IT handles personal requests when work requests allow.'}{' '}
                      There’s no deadline, and you’ll be told when IT gets to it.
                    </Alert>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Spam trap: hidden from people, irresistible to bots. */}
          <div className="absolute -left-[9999px]" aria-hidden>
            <label>
              Website
              <input type="text" name="website" tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          {/* On phones the buttons stay in reach, just above the bottom bar. */}
          <div className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom))] z-20 -mx-4 flex gap-2 border-t border-line bg-bg/95 px-4 py-3 backdrop-blur md:static md:mx-0 md:border-0 md:bg-transparent md:px-0 md:py-1 md:backdrop-blur-none">
            <Button type="submit" size="lg" className="flex-1 md:flex-none" loading={submit.isPending}>
              Send to IT
            </Button>
            <Button variant="ghost" size="lg" onClick={goBack}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
